// shared-components/lm-doc-action-cell/lm-doc-action-cell.component.ts
// Angular AG Grid cell renderer for Edit + Download + Delete actions on
// policy-document rows. Uses [lmTooltip] for consistent tooltip styling.

import { Component, ChangeDetectionStrategy, ChangeDetectorRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-enterprise';
import { LmTooltipDirective } from '../tooltip/tooltip.directive';

@Component({
  selector: 'ers-lm-doc-action-cell',
  standalone: true,
  imports: [CommonModule, LmTooltipDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="lm-action-container">
      <button [style.display]="hidePreview ? 'none' : 'flex'" class="lm-action-btn"
              [lmTooltip]="'Preview'"
              tooltipPosition="top"
              data-action="preview"
              [attr.data-id]="rowId">
        <span class="material-icons" style="font-size:18px;color:#2469A0;line-height:1;display:flex">visibility</span>
      </button>

      <button class="lm-action-btn"
              [lmTooltip]="'Download'"
              tooltipPosition="top"
              data-action="download"
              [attr.data-id]="rowId">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <path d="M14.0156 3.58203L14.1326 3.58903C14.3558 3.61536 14.5637 3.71611 14.7226
                   3.87504C14.8815 4.03397 14.9823 4.24181 15.0086 4.46503L15.0156 4.58203V8.58203
                   L15.0206 8.73203C15.0564 9.2082 15.2614 9.65586 15.5985 9.99406C15.9356 10.3323
                   16.3826 10.5387 16.8586 10.576L17.0156 10.582H21.0156L21.1326 10.589C21.3558
                   10.6154 21.5637 10.7161 21.7226 10.875C21.8815 11.034 21.9823 11.2418 22.0086
                   11.465L22.0156 11.582V20.582C22.0157 21.3472 21.7233 22.0835 21.1983 22.6403
                   C20.6734 23.197 19.9555 23.5321 19.1916 23.577L19.0156 23.582H9.01562C8.25041
                   23.5821 7.51411 23.2897 6.95736 22.7647C6.40062 22.2398 6.06552 21.5219 6.02063
                   20.758L6.01563 20.582V6.58203C6.01558 5.81682 6.30795 5.08052 6.83291 4.52377
                   C7.35787 3.96702 8.07573 3.63192 8.83962 3.58703L9.01562 3.58203H14.0156Z
                   M14.0156 11.582C13.7504 11.582 13.4961 11.6874 13.3085 11.8749C13.121 12.0625
                   13.0156 12.3168 13.0156 12.582V16.167L12.2226 15.375C12.0504 15.2029 11.8213
                   15.0994 11.5783 15.0841C11.3353 15.0689 11.095 15.1428 10.9026 15.292L10.8086
                   15.375C10.6212 15.5626 10.5158 15.8169 10.5158 16.082C10.5158 16.3472 10.6212
                   16.6015 10.8086 16.789L13.3086 19.289L13.3526 19.331L13.4206 19.386L13.5306
                   19.457L13.6446 19.511L13.7496 19.546L13.8996 19.576L14.0156 19.582L14.1326
                   19.575L14.2496 19.555L14.3576 19.522L14.4386 19.488L14.5366 19.436L14.6286
                   19.372L14.7226 19.289L17.2226 16.789C17.4101 16.6015 17.5154 16.3472 17.5154
                   16.082C17.5154 15.8169 17.4101 15.5626 17.2226 15.375L17.1286 15.292C16.9362
                   15.1428 16.696 15.0689 16.4529 15.0841C16.2099 15.0994 15.9808 15.2029 15.8086
                   15.375L15.0156 16.166V12.582C15.0156 12.3371 14.9257 12.1007 14.7629 11.9177
                   C14.6002 11.7346 14.3759 11.6177 14.1326 11.589L14.0156 11.582Z
                   M17.0146 4.58103L21.0156 8.58203H17.0156L17.0146 4.58103Z"
                fill="#18669F"/>
        </svg>
      </button>

      <button class="lm-action-btn"
              [lmTooltip]="'Delete'"
              tooltipPosition="top"
              data-action="delete"
              [attr.data-id]="rowId">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <path d="M7.616 20.0005C7.15533 20.0005 6.771 19.8465 6.463 19.5385C6.155 19.2305
                   6.00067 18.8458 6 18.3845V6.00047H5V5.00047H9V4.23047H15V5.00047H19V6.00047
                   H18V18.3855C18 18.8455 17.846 19.2298 17.538 19.5385C17.23 19.8471 16.8453
                   20.0011 16.384 20.0005H7.616ZM9.808 17.0005H10.808V8.00047H9.808V17.0005Z
                   M13.192 17.0005H14.192V8.00047H13.192V17.0005Z" fill="#D01A2C"/>
        </svg>
      </button>
    </div>
  `,
  styles: [`
    .lm-action-container {
      display: flex;
      align-items: center;
      gap: 4px;
      height: 100%;
    }
    .lm-action-btn {
      background: none;
      border: none;
      cursor: pointer;
      padding: 4px;
      border-radius: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background 0.15s;
    }
    .lm-action-btn:hover { background: rgba(0,0,0,0.06); }
  `],
})
export class LmDocActionCellComponent implements ICellRendererAngularComp {
  private cdr = inject(ChangeDetectorRef);
  rowId: string | number = '';
  hidePreview = false;

  agInit(params: ICellRendererParams): void {
    this.rowId = params.data?.id ?? '';
    this.hidePreview = params.context?.hidePreview ?? false;
    this.cdr.markForCheck();
  }

  refresh(params: ICellRendererParams): boolean {
    this.rowId = params.data?.id ?? '';
    this.hidePreview = params.context?.hidePreview ?? false;
    this.cdr.markForCheck();
    return true;
  }
}
